import { useEffect, useRef, useState } from "react";

import type { WsMessage } from "../shared/types";

type Listener = (msg: WsMessage) => void;

class WsClient {
	private ws: WebSocket | null = null;
	private listeners = new Set<Listener>();
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private closedByUs = false;
	public status: "connecting" | "open" | "closed" = "closed";
	public statusListeners = new Set<(s: WsClient["status"]) => void>();

	connect() {
		if (this.ws) return;
		this.closedByUs = false;
		this.setStatus("connecting");
		const proto = location.protocol === "https:" ? "wss:" : "ws:";
		const url = `${proto}//${location.host}/ws`;
		const ws = new WebSocket(url);
		this.ws = ws;
		ws.onopen = () => {
			this.setStatus("open");
			ws.send(
				JSON.stringify({
					type: "subscribe",
					topics: ["events", "buys", "stores", "grouping"],
				}),
			);
		};
		ws.onmessage = (ev) => {
			try {
				const msg = JSON.parse(String(ev.data)) as WsMessage;
				for (const l of this.listeners) l(msg);
			} catch {}
		};
		ws.onclose = () => {
			this.ws = null;
			this.setStatus("closed");
			// Logging out closes the socket deliberately; reconnecting would just
			// retry a 401 upgrade every 1.5s for as long as the tab stays open.
			if (this.closedByUs) return;
			this.reconnectTimer = setTimeout(() => this.connect(), 1500);
		};
		ws.onerror = () => ws.close();
	}

	/** Drop the socket without the automatic reconnect. */
	disconnect() {
		this.closedByUs = true;
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		this.ws?.close();
		this.ws = null;
		this.setStatus("closed");
	}

	private setStatus(s: WsClient["status"]) {
		this.status = s;
		for (const l of this.statusListeners) l(s);
	}

	subscribe(l: Listener): () => void {
		this.listeners.add(l);
		return () => this.listeners.delete(l);
	}
}

export const wsClient = new WsClient();

export function useWs(listener: Listener) {
	const ref = useRef(listener);
	ref.current = listener;
	useEffect(() => {
		wsClient.connect();
		return wsClient.subscribe((m) => ref.current(m));
	}, []);
}

export function useWsStatus() {
	const [status, setStatus] = useState(wsClient.status);
	useEffect(() => {
		wsClient.connect();
		const l = (s: typeof status) => setStatus(s);
		wsClient.statusListeners.add(l);
		return () => {
			wsClient.statusListeners.delete(l);
		};
	}, []);
	return status;
}
